import {MAX_RETRIES, PAYMENT_METHOD, RETRY_DELAY_MS} from "./constants"
import notify, {NOTIFICATION_TYPES, NotificationOptions} from "../notify"
import {isEqual} from "lodash/lang"
import {BasePayment, TokenCCPayment} from "./models"
import {
  createCreditCardTransaction, createHostedCheckoutTransaction,
  createNoTypeTransaction,
  createPostPayTransaction,
  createSavedCreditCardTransaction,
} from "../../api/api-billing"
import {appMng} from "../../main"

function onBoaCompraChargeFailure(code, message) {
  let notification = {
    message: `Ocorreu um erro no pagamento com o BoaCompra: ${message}.`,
    type: NOTIFICATION_TYPES.ERROR,
  }

  switch (code) {
    case 210104:
    case 210114:
    case 210225:
      notification["message"] = `Ocorreu um erro interno no processo de pagamento, código: ${code}.`
      break
    case 210204:
      notification["message"] = `O preço da compra não pode ser menor que R$ ${0.20}.`
      break
    case 210214:
      notification["message"] = `Foi selecionado mais parcelas do que o permitido.`
      break
    case 210216:
      notification["message"] = `Não foi possível utilizar o cartão de crédito informado.`
      break
    case 210601:
      notification["message"] = `O método de pagamento selecionado não é disponível para esta compra.`
      break
    case 210602:
      notification["message"] = `O valor da compra está acima do limite permitido: R$ ${10000}.`
      break
    case 210603:
      notification["message"] = `Usuário impedido de realizar compras pelo BoaCompra.`
      break
    case 210221:
      notification["message"] = `Campos de CVV e data de expiração estão faltando.`
      break
    case 210320:
      notification["message"] = `O endereço do usuário é inválido, por favor revise na página de configurações de usuário.`
      break
    case 210322:
      notification["message"] = `A rua do usuário é inválida, por favor revise na página de configurações de usuário.`
      break
    case 210324:
      notification["message"] = `O número do usuário é inválido, por favor revise na página de configurações de usuário.`
      break
    case 210326:
      notification["message"] = `O complemento do usuário é inválido, por favor revise na página de configurações de usuário.`
      break
    case 210328:
      notification["message"] = `O distrito do usuário é inválido, por favor revise na página de configurações de usuário.`
      break
    case 210330:
      notification["message"] = `O estado do usuário é inválido, por favor revise na página de configurações de usuário.`
      break
    case 210332:
      notification["message"] = `A cidade do usuário é inválida, por favor revise na página de configurações de usuário.`
      break
    case 210334:
      notification["message"] = `O país do usuário é inválido, por favor revise na página de configurações de usuário.`
      break
    case 210336:
      notification["message"] = `O CEP do usuário é inválido, por favor revise na página de configurações de usuário.`
      break
    case -1:
      notification["message"] = `Ocorreu algum problema ao solicitar o pagamento. Tente novamente mais tarde.`
      break
    default:
      break
  }

  notify(new NotificationOptions(notification))
}

const _PaymentMixin = Symbol.for("_PaymentMixin")
const InitPayment = Symbol.for("InitPayment")
const SetShoppingCart = Symbol.for("SetShoppingCart")
const SetResumeCardData = Symbol.for("SetResumeCardData")
const SetResumePayerInfo = Symbol.for("SetResumePayerInfo")
const SetResumeNfeInfo = Symbol.for("SetResumeNfeInfo")
const SetResumeInstallmentsInfo = Symbol.for("SetResumeInstallmentsInfo")
const SetPaymentMethod = Symbol.for("SetPaymentMethod")
const SetPaymentData = Symbol.for("SetPaymentData")
const SetCdPayerInfo = Symbol.for("SetCdPayerInfo")
const SetCdNfeInfo = Symbol.for("SetCdNfeInfo")

export const paymentMixin = {
  data: () => ({
    paymentMixin: {
      isPaying: false,

      shoppingCart: null,

      resumeInstallmentsInfo: null,
      resumeCardData: null,
      resumePayerInfo: null,
      resumeNfeInfo: null,

      paymentMethod: null,
      paymentData: new BasePayment(),

      cdPayerInfo: null,
      cdNfeInfo: null,
    },

    intPayAttempts: 0,
  }),
  provide() {
    return {
      [_PaymentMixin]: () => this.paymentMixin,
      [InitPayment]: this.initPayment,
      [SetShoppingCart]: this.setShoppingCart,
      [SetResumeCardData]: this.setResumeCardData,
      [SetResumePayerInfo]: this.setResumePayerInfo,
      [SetResumeNfeInfo]: this.setResumeNfeInfo,
      [SetResumeInstallmentsInfo]: this.setResumeInstallmentsInfo,
      [SetPaymentMethod]: this.setPaymentMethod,
      [SetPaymentData]: this.setPaymentData,
      [SetCdPayerInfo]: this.setCdPayerInfo,
      [SetCdNfeInfo]: this.setCdNfeInfo,
    }
  },
  methods: {
    async createTransaction(authToken = null) {
      let payload = {
        int_installments: this.paymentMixin.paymentData.intInstallments,
        ...((this.paymentMixin.cdPayerInfo) && {cd_payer_info: this.paymentMixin.cdPayerInfo}),
        ...((this.paymentMixin.cdNfeInfo) && {cd_nfe_info: this.paymentMixin.cdNfeInfo}),
      }
      if (this.paymentMixin.paymentData instanceof TokenCCPayment)
        payload = {
          ...payload,
          cd_cc_token: this.paymentMixin.paymentData.cdCcToken,
          str_cc_brand: this.paymentMixin.paymentData.strCcBrand,
          bl_cc_save: this.paymentMixin.paymentData.blCcSave,
        }

      const idShoppingCart = this.paymentMixin.shoppingCart["id_shopping_cart"]
      switch (this.paymentMixin.paymentMethod) {
        case PAYMENT_METHOD.CREDIT_CARD:
          return createCreditCardTransaction(authToken, idShoppingCart, payload)
        case PAYMENT_METHOD.SAVED_CREDIT_CARD:
          return createSavedCreditCardTransaction(authToken, idShoppingCart, payload)
        case PAYMENT_METHOD.POST_PAY:
          return createPostPayTransaction(authToken, idShoppingCart)
        case PAYMENT_METHOD.NO_TYPE:
          return createNoTypeTransaction(authToken, idShoppingCart)
        case PAYMENT_METHOD.BOACOMPRA:
          return createHostedCheckoutTransaction(authToken, idShoppingCart)
        default:
          notify(new NotificationOptions({
            message: "Método de pagamento inválido, por favor revise a etapa do método de pagamento",
            type: NOTIFICATION_TYPES.ERROR,
          }))
          return Promise.reject(null)
      }
    },

    async initPayment(authToken) {
      this.paymentMixin.isPaying = true
      return new Promise((resolve, reject) => {
        this.createTransaction(authToken)
          .then(res => {
            appMng.setShoppingCartLength(0)
            resolve(res)
          })
          .catch(err => {
            this.onCreateTransactionFailure(err, () => {
              this.initPaymentRetry(authToken, resolve, reject)
            }, reject)
          })
      })
    },

    initPaymentRetry(authToken, resolve, reject) {
      this.createTransaction(authToken)
        .then(res => {
          appMng.setShoppingCartLength(0)
          resolve(res)
        })
        .catch(err => {
          this.onCreateTransactionFailure(err, () => {
            this.initPaymentRetry(authToken, resolve, reject)
          }, reject)
        })
    },

    // Helpers
    setShoppingCart(shoppingCart) {
      this.paymentMixin.shoppingCart = shoppingCart
    },
    setResumeInstallmentsInfo(resumeInstallmentsInfo) {
      this.paymentMixin.resumeInstallmentsInfo = resumeInstallmentsInfo
    },
    setResumeCardData(resumeCardData) {
      this.paymentMixin.resumeCardData = resumeCardData
    },
    setResumePayerInfo(resumePayerInfo) {
      this.paymentMixin.resumePayerInfo = resumePayerInfo
    },
    setResumeNfeInfo(resumeNfeInfo) {
      this.paymentMixin.resumeNfeInfo = resumeNfeInfo
    },
    setPaymentMethod(paymentMethod) {
      this.paymentMixin.paymentMethod = paymentMethod
    },
    setPaymentData(paymentData) {
      if (!(paymentData instanceof BasePayment))
        notify(new NotificationOptions({
          message: "Dados de pagamento inválidos",
          type: NOTIFICATION_TYPES.ERROR,
        }))
      this.paymentMixin.paymentData = paymentData
    },
    setCdPayerInfo(cdPayerInfo) {
      this.paymentMixin.cdPayerInfo = cdPayerInfo
    },
    setCdNfeInfo(cdNfeInfo) {
      this.paymentMixin.cdNfeInfo = cdNfeInfo
    },

    onCreateTransactionFailure(err, paymentChargeCallb, reject) {
      if (err.response) {
        if (!err.response.data.data) {
          notify(new NotificationOptions({
            message: err.response.data.message,
            type: NOTIFICATION_TYPES.ERROR,
          }))
          this.paymentMixin.isPaying = false
          reject(err)
        } else if (
          isEqual(err.response.data.data.code, -1) &&
          !isEqual(this.intPayAttempts, MAX_RETRIES)
        ) {
          this.intPayAttempts--
          setTimeout(() => {
            paymentChargeCallb()
          }, RETRY_DELAY_MS)
        } else if (
          err.response.data.data.code &&
          err.response.data.message
        ) {
          onBoaCompraChargeFailure(err.response.data.data.code, err.response.data.message)
          this.paymentMixin.isPaying = false
          reject(err)
        } else {
          notify(new NotificationOptions({
            message: `${err.response.data.message}: ${JSON.stringify(err.response.data.data)}`,
            type: NOTIFICATION_TYPES.ERROR,
          }))
          this.paymentMixin.isPaying = false
          reject(err)
        }
      } else {
        notify(new NotificationOptions({
          message: "Erro de conexão, verifique sua internet e tente novamente",
          type: NOTIFICATION_TYPES.ERROR,
        }))
        this.paymentMixin.isPaying = false
        reject(err)
      }
    },
  },
}
